import React, { useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  ShieldCheck,
  Zap,
  Activity,
  DollarSign,
  Users,
  Search,
  Globe,
  Lock,
  ArrowUpRight,
  Sparkles,
  AlertTriangle,
  FileText,
  Clock,
  Layers,
  ChevronRight,
  Sliders
} from 'lucide-react';
import { AppView } from '../../types';
import { analyticsEngine } from '../../services/analyticsEngine';

interface Props {
  onNavigateToView?: (view: AppView) => void;
}

export const ExecutiveCommandCenter: React.FC<Props> = ({ onNavigateToView }) => {
  const [telemetry] = useState(analyticsEngine.getTelemetry());
  const marketOpps = analyticsEngine.getMarketOpportunities();

  const finPrivacyCheck = analyticsEngine.runFinancialValuesMustNotReachGA4Check();
  const mathExpCheck = analyticsEngine.runFinancialMathIsNeverExperimentCheck();
  const rankCheck = analyticsEngine.runOrganicRankingIndependenceCheck();

  return (
    <div className="space-y-8 pb-12">
      {/* Executive Hero Cockpit */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold border border-blue-500/30">
              <BarChart3 className="w-3.5 h-3.5" />
              YamooPay Executive Command Center • One Screen Business Cockpit
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">Executive Business & Product Intelligence</h1>
            <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
              Real-time measurement of active users, meaningful financial tool sessions, organic search conversion, unit economics, and privacy-governed product telemetry.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => onNavigateToView?.('analytics-privacy-quality')}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold transition-all border border-slate-700 flex items-center gap-1.5 cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> Privacy & Quality Center
            </button>
            <button
              onClick={() => onNavigateToView?.('event-registry')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Layers className="w-4 h-4" /> Event Registry →
            </button>
          </div>
        </div>

        {/* Status Line */}
        <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700/80 flex flex-wrap items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-emerald-400 font-bold">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span> First-Party Warehouse: {telemetry.firstPartyWarehouseStatus}
            </span>
            <span className="text-slate-500">|</span>
            <span className="text-slate-300">GA4 Destination Sync: <strong className="text-emerald-400 font-bold">{telemetry.ga4SyncHealthStatus}</strong></span>
          </div>

          <div className="flex items-center gap-2 text-slate-400 font-mono">
            <span>Time to First Useful Result: <strong className="text-white">{telemetry.timeToFirstUsefulResultSec}s</strong></span>
          </div>
        </div>
      </div>

      {/* Primary Executive KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Active Users (DAU / MAU)</span>
            <Users className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            {(telemetry.dauCount / 1000).toFixed(1)}k / {(telemetry.mauCount / 1000).toFixed(0)}k
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
            <span>Stickiness Ratio: <strong className="text-blue-700 font-mono">{telemetry.stickinessDauMauPct}%</strong></span>
            <span className="text-emerald-700 font-bold">+12.4% MoM</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Successful Financial Sessions</span>
            <Zap className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            {(telemetry.successfulFinancialSessions30d / 1000).toFixed(1)}k
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
            <span>Completion Rate: <strong className="text-emerald-700 font-mono">{telemetry.calculationCompletionRatePct}%</strong></span>
            <span className="text-slate-400">30D Rollup</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Organic Search Traffic</span>
            <Search className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            {(telemetry.organicClicks30d / 1000).toFixed(0)}k Clicks
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
            <span>Organic Reg Rate: <strong className="text-purple-700 font-mono">{telemetry.registrationRatePct}%</strong></span>
            <span className="text-emerald-700 font-bold">+18.2% YoY</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Total Gross Revenue (30D)</span>
            <DollarSign className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            ${telemetry.totalRevenue30dUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
            <span>Rev / Qual Session: <strong className="text-emerald-700 font-mono">${telemetry.revenuePerQualifiedSessionUsd.toFixed(3)}</strong></span>
          </div>
        </div>
      </div>

      {/* 4 Core Intelligence Modules Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Module A: Funnel & Retention Intelligence */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600" />
              Product Funnels & Cohort Retention
            </h3>
            <span className="text-xs font-bold text-blue-600 font-mono">
              24.8% Returning Users
            </span>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Tracks user conversion from landing entrance to calculation completion, scenario saving, and account activation.
          </p>
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1">
            <div className="flex justify-between text-slate-700">
              <span>Global Calculation Completion:</span>
              <span className="font-bold text-emerald-700">78.4%</span>
            </div>
            <div className="flex justify-between text-slate-700">
              <span>Day 30 Returning Calculation Rate:</span>
              <span className="font-bold text-blue-700">24.8%</span>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView?.('funnel-cohort-intelligence')}
            className="w-full text-center py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 transition-colors cursor-pointer flex items-center justify-center gap-1"
          >
            Explore Funnels & Cohorts <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Module B: Attribution & Controlled Experiments */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              Acquisition Attribution & A/B Testing
            </h3>
            <span className="text-xs font-bold text-purple-600 font-mono">
              Organic Search #1
            </span>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Multi-touch channel attribution and feature flag experimentation with deterministic financial math guardrails.
          </p>
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1">
            <div className="flex justify-between text-slate-700">
              <span>Organic First-Touch Attribution:</span>
              <span className="font-bold text-purple-700">64.2%</span>
            </div>
            <div className="flex justify-between text-slate-700">
              <span>Active A/B Experiments:</span>
              <span className="font-bold text-slate-900">1 Experiment (Passed)</span>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView?.('attribution-experimentation')}
            className="w-full text-center py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 transition-colors cursor-pointer flex items-center justify-center gap-1"
          >
            Explore Attribution & Experiments <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Market Opportunity Analytics Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> Unserved Demand Intelligence
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">
              Global Market Opportunity Engine
            </h2>
          </div>

          <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold">
            {telemetry.unsupportedDemandEventsCount.toLocaleString()} Monthly Unsupported Search Events
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {marketOpps.map((opp) => (
            <div key={opp.countryCode} className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900 text-sm">{opp.countryName}</h3>
                <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${
                  opp.status === 'LIVE_SUPPORTED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                }`}>
                  {opp.status}
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">{opp.recommendedAction}</p>
              <div className="pt-2 border-t border-slate-200/60 flex justify-between text-[11px] font-mono text-slate-500">
                <span>Monthly Search Est: <strong>{opp.monthlyOrganicDemandEstimate.toLocaleString()}</strong></span>
                <span className="text-emerald-700 font-bold">+${opp.monetizationUpsideUsdMonthly}/mo</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Executive Unit Test Integrity Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-xl space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-emerald-400" />
            <h3 className="font-bold text-sm">Automated Financial Analytics & Governance Certification</h3>
          </div>
          <span className="px-2.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-xs font-bold">
            All System Controls Passing
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-1.5">
            <div className="font-bold text-emerald-400">1. Financial Values Privacy Test</div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              {finPrivacyCheck.message}
            </p>
          </div>

          <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-1.5">
            <div className="font-bold text-emerald-400">2. Financial Math Experiment Guardrail</div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              {mathExpCheck.message}
            </p>
          </div>

          <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-1.5">
            <div className="font-bold text-emerald-400">3. Organic Ranking Firewall</div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              {rankCheck.message}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
